const toArray = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const flatSplitLocations = (value) =>
  toArray(value).flatMap((loc) =>
    typeof loc === "string"
      ? [loc.trim()].filter(Boolean)
      : []
  );

const SENIORITY_TO_CONTACTOUT = {
  intern:      "Entry",
  entry_level: "Entry",
  associate:   "Entry",
  mid_level:   "Entry",
  senior:      "Entry",
  lead:        "Manager",
  manager:     "Manager",
  director:    "Director",
  vp:          "VP",
  c_level:     "C-Level",
};

const toContactOutRange = (v) => (typeof v === "string" ? v.replace(/-/g, "_") : v);

const toExpApiValue = (v) => {
  if (typeof v === "string" && v.toLowerCase() === "less than 1 year") return "0_1";
  return toContactOutRange(v);
};

const toNumberArray = (value) =>
  toArray(value).map(Number).filter(Number.isFinite);

export const buildExternalFiltersPayload = (filters, page = 1) => {
  const payload = {
    page: page || 1,
  };

  if (filters?.name) payload.name = filters.name;
  if (filters?.keyword) payload.keyword = filters.keyword;

  const jobTitle = toArray(filters?.jobTitleInclude);
  if (jobTitle.length) payload.job_title = jobTitle;

  const excludeJobTitles = toArray(filters?.jobTitleExclude);
  if (excludeJobTitles.length) payload.exclude_job_titles = excludeJobTitles;

  const jobFunction = toArray(filters?.jobFunction);
  if (jobFunction.length) payload.job_function = jobFunction;

  const seniority = [
    ...new Set(
      toArray(filters?.seniority)
        .map((s) => SENIORITY_TO_CONTACTOUT[s] || s)
        .filter(Boolean)
    ),
  ];
  if (seniority.length) payload.seniority = seniority;

  // if (filters?.matchExperience) payload.match_experience = filters.matchExperience;
  if (filters?.currentTitlesOnly) payload.current_titles_only = true;
  if (filters?.includeRelatedTitles) payload.include_related_job_titles = true;

  const skills = toArray(filters?.skill);
  if (skills.length) payload.skills = skills;

  const languages = toArray(filters?.language).map((lang) => ({ language: lang }));
  if (languages.length) payload.languages = languages;

  const education = toArray(filters?.school);
  if (education.length) payload.education = education;

  const location = flatSplitLocations(filters?.location);
  if (location.length) {
    payload.location = location;
    payload.location_radius = filters?.locationRadius || 50;
  }

  const currentWorkLocation = flatSplitLocations(filters?.currentWorkLocation);
  if (currentWorkLocation.length) payload.current_work_location = currentWorkLocation;

  const pastWorkLocation = flatSplitLocations(filters?.pastWorkLocation);
  if (pastWorkLocation.length) payload.past_work_location = pastWorkLocation;

  const remoteLocation = flatSplitLocations(filters?.remoteLocation);
  if (remoteLocation.length) payload.remote_location = remoteLocation;

  const company = toArray(filters?.companyInclude);
  if (company.length) payload.company = company;

  const excludeCompanies = toArray(filters?.companyExclude);
  if (excludeCompanies.length) payload.exclude_companies = excludeCompanies;

  if (filters?.companyCurrentOnly) {
    payload.company_filter = "current";
    payload.current_company_only = true;
  } else if (filters?.companyIncludeRelated) {
    payload.company_filter = "both";
  }

  const domain = toArray(filters?.domain);
  if (domain.length) payload.domain = domain;

  const industry = toArray(filters?.industry);
  if (industry.length) payload.industry = industry;

  const companySize = toArray(filters?.companySize).map(toContactOutRange);
  if (companySize.length) payload.company_size = companySize;

  const yearsOfExp = toArray(filters?.yearsOfExp).map(toExpApiValue);
  if (yearsOfExp.length) payload.years_of_experience = yearsOfExp;

  const yearsOfExpCurrentRole = toArray(filters?.yearsOfExpCurrentRole).map(toExpApiValue);
  if (yearsOfExpCurrentRole.length) payload.years_in_current_role = yearsOfExpCurrentRole;

  const contactAvailability = toArray(filters?.contactAvailability);
  if (contactAvailability.length) payload.contact_availability = contactAvailability;

  return payload;
};

const SORT_LABEL = {
  relevant: "Most Relevant",
  last_active: "Last Active",
  new_skill_listing: "New Skill Listing",
};

export const buildInternalFiltersPayload = (
  filters,
  page,
  sortOrder = "relevant",
  skillName = "",
  limit = 20
) => ({
  skillName,
  sortBy: SORT_LABEL[sortOrder] ?? "Most Relevant",
  page,
  limit,
  filters: {
    name: filters?.name || "",
    keyword: filters?.keyword || "",
    jobTitleInclude: toArray(filters?.jobTitleInclude),
    jobTitleExclude: toArray(filters?.jobTitleExclude),
    location: toArray(filters?.location),
    currentWorkLocation: toArray(filters?.currentWorkLocation),
    pastWorkLocation: toArray(filters?.pastWorkLocation),
    remoteLocation: toArray(filters?.remoteLocation),
    skills: toArray(filters?.skills ?? filters?.skill),
    education: toArray(filters?.education ?? filters?.school),
    languages: toArray(filters?.languages ?? filters?.language),
    yearsOfExperience: toArray(filters?.yearsOfExperience ?? filters?.yearsOfExp),
    yearsOfExperienceInCurrentRole: toArray(
      filters?.yearsOfExperienceInCurrentRole ?? filters?.yearsOfExpCurrentRole
    ),
  },
});

export const buildMarketplaceFiltersPayload = (filters, page, sortOrder = "relevant") => ({
  page,
  sortBy: SORT_LABEL[sortOrder] ?? "Most Relevant",
  filters: {
    keyword: filters?.keyword || "",
    name: filters?.name || "",
    jobTitleInclude: toArray(filters?.jobTitleInclude),
    jobTitleExclude: toArray(filters?.jobTitleExclude),
    companyInclude: toArray(filters?.companyInclude),
    companyExclude: toArray(filters?.companyExclude),
    location: toArray(filters?.location),
    skills: toArray(filters?.skills ?? filters?.skill),
    education: toArray(filters?.education ?? filters?.school),
    languages: toArray(filters?.languages ?? filters?.language),
    yearsOfExperience: toArray(filters?.yearsOfExperience ?? filters?.yearsOfExp),
    companySizeIds: toArray(filters?.companySizeIds ?? filters?.companySize),
    yearsOfExperienceInCurrentRole: toArray(
      filters?.yearsOfExperienceInCurrentRole ?? filters?.yearsOfExpCurrentRole
    ),
    ...(filters?.ratingMin != null ? { ratingMin: filters.ratingMin } : {}),
    ...(filters?.minRate != null ? { minRate: filters.minRate } : {}),
    ...(filters?.maxRate != null ? { maxRate: filters.maxRate } : {}),
  },
});

export const buildNonSkillRadarPmtFiltersPayload = (
  filters,
  page,
  sortOrder = "relevant"
) => {
  try {
    return {
      page,
      sortBy: SORT_LABEL[sortOrder] ?? "Most Relevant",
      filters: {
        name: filters?.name || "",
        jobTitleInclude: toArray(filters?.jobTitleInclude),
        location: toArray(filters?.location),
        CityId: toNumberArray(filters?.cityId),
        RegionId: toNumberArray(filters?.regionId),
        CountryId: toNumberArray(filters?.countryId),
        GigType: filters?.skillListingType || null,
        CompanyName: toArray(filters?.companyInclude),
        BudgetMin: filters?.budgetMin || null,
        BudgetMax: filters?.budgetMax || null,
        BudgetType: filters?.budgetType || null,
        VisaStatusId: toNumberArray(filters?.visaStatusId),
        Experience: toArray(filters?.experienceLevel),
        skills: toArray(filters?.skills ?? filters?.skill),
        yearsOfExperience: toArray(filters?.yearsOfExperience ?? filters?.yearsOfExp),
        yearsOfExperienceInCurrentRole: toArray(
          filters?.yearsOfExperienceInCurrentRole ?? filters?.yearsOfExpCurrentRole
        ),
      },
    };
  } catch (error) {
    console.warn("Failed to build PMT filter payload", error);

    return {
      page,
      sortBy: SORT_LABEL[sortOrder] ?? "Most Relevant",
      filters: {},
    };
  }
};
